import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';

export const Route = createFileRoute('/pause-contract')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const { pause: pauseEvmMutation, isPausedQuery: isEvmPausedQuery } =
    useEvmPause();
  const { pause: pauseTronMutation, isPausedQuery: isTronPausedQuery } =
    useTronPause();

  // --- Mutations ---
  const { mutate: handlePause, isPending: isPausing } = useMutation({
    mutationFn: async (network: 'evm' | 'tron') => {
      switch (network) {
        case 'evm':
          await pauseEvmMutation.mutateAsync();
          break;
        case 'tron':
          await pauseTronMutation.mutateAsync();
          break;
      }
    },
    onSuccess: () => toast.success(t('pauseContract.contractCallSuccess')),
    onError: (error) =>
      toast.error(
        `${t('pauseContract.failedToPauseContract')}: ${error.message}`,
      ),
  });

  return (
    <div className="min-h-svh grid place-items-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>{t('pauseContract.contractControl')}</CardTitle>
          <CardDescription>{t('pauseContract.pauseContract')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Pause / Unpause Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              {t('pauseContract.evmContractPause')}
            </h3>
            <div className="grid gap-4">
              <dl>
                <dt className="text-sm text-gray-500">
                  {t('pauseContract.isPaused')}
                </dt>
                <dd>
                  {isEvmPausedQuery.error?.message || isEvmPausedQuery.data
                    ? t('common.yes')
                    : t('common.no')}
                </dd>
              </dl>
              <Button
                onClick={() => handlePause('evm')}
                disabled={isPausing}
                variant="destructive"
              >
                {isPausing && <Spinner className="mr-2" />}
                {t('pauseContract.pauseEvmContract')}
              </Button>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              {t('pauseContract.tronContractPause')}
            </h3>
            <div className="grid gap-4">
              <dl>
                <dt className="text-sm text-gray-500">
                  {t('pauseContract.isPaused')}
                </dt>
                <dd>
                  {isTronPausedQuery.error?.message || isTronPausedQuery.data
                    ? t('common.yes')
                    : t('common.no')}
                </dd>
              </dl>
              <Button
                onClick={() => handlePause('tron')}
                disabled={isPausing}
                variant="destructive"
              >
                {isPausing && <Spinner className="mr-2" />}
                {t('pauseContract.pauseTronContract')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
